import { CommonModule } from '@angular/common';
import { Component, ElementRef, HostBinding, HostListener, Inject, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MaterialModule } from 'src/layout/material.module';
import { ModalService } from '../../services/modal.service';
import { PopupService } from '../../services/popup.service';
import { AppVisualContextService } from '../../services/app-visual-context.service';
import { AppAccent } from 'src/app/core/types/app-accent.type';

export type ModalBeforeConfirm = () => boolean;
// Retornar false impede o fechamento padrão do botão de cancelar (o chamador assume o encerramento do modal).
export type ModalBeforeCancel = () => boolean;

@Component({
  selector: 'app-modal',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.css',
})
export class ModalComponent implements OnInit, OnDestroy {
  @ViewChild('closeConfirmationDialog') closeConfirmationDialog: TemplateRef<any>;

  @Input() accent?: AppAccent;
  @Input({ required: true }) title: string = '';
  @Input() subtitle: string = '';
  @Input() buttonCloseDisabled: boolean = false;
  @Input() buttonCancelDisabled: boolean = false;
  @Input() buttonConfirmUnlocked: boolean = false;
  @Input() buttonConfirmDisabled: boolean = false;
  @Input() buttonCancelLabel: string = 'Cancelar';
  @Input() buttonConfirmLabel?: string;
  @Input() buttonConfirmMobileLabel?: string;
  @Input() validateForms?: FormGroup[];
  @Input() msgFormInvalid: string = 'Por favor, preencha o formulário antes de confirmar!';
  @Input() closeConfirmation: boolean = false;
  @Input() closeConfirmationMessage: string = 'Existem alterações não salvas. Tem certeza que deseja sair?';
  @Input() floatingBarTemplate?: TemplateRef<any>;
  @Input() beforeConfirm?: ModalBeforeConfirm;
  @Input() beforeCancel?: ModalBeforeCancel;

  // Serviços injetados via constructor
  private confirmationDialogRef: MatDialogRef<any> | null = null;

  private capturedAccent: AppAccent;

  @HostBinding('class') get visualClass(): string {
    return this.visualContext.classForInputs(this.accent, this.capturedAccent);
  }

  constructor(
    private dialogService: MatDialog,
    private modalService: ModalService,
    private popupService: PopupService,
    protected modalRef: MatDialogRef<ModalComponent>,
    @Inject(MAT_DIALOG_DATA) private data: any,
    private visualContext: AppVisualContextService,
    private element: ElementRef<HTMLElement>
  ) {
    this.capturedAccent = this.visualContext.accentForInputs(this.data?.accent);
    // Inicializa inputs a partir dos dados injetados
    if (this.data) {
      this.title = this.data.title || this.title;
      this.accent = this.data.accent ?? this.accent;
      this.buttonCancelLabel = this.data.buttonCancelLabel || this.buttonCancelLabel;
      this.buttonConfirmLabel = this.data.buttonConfirmLabel || this.buttonConfirmLabel;
      this.buttonCancelDisabled = this.data.buttonCancelDisabled || this.buttonCancelDisabled;
    }
    this.modalRef.disableClose = true;

    this.modalRef.afterOpened().subscribe(() => {
      const dialogElement = document.querySelector('.mat-mdc-dialog-container');
      if (dialogElement) {
        dialogElement.setAttribute('role', 'dialog');
        dialogElement.setAttribute('aria-modal', 'true');
      }
    });
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event) {
    const topDialog = this.dialogService.openDialogs[this.dialogService.openDialogs.length - 1];
    if (this.buttonCloseDisabled || (topDialog !== this.modalRef && topDialog !== this.confirmationDialogRef)) return;
    const keyboardEvent = event as KeyboardEvent;
    keyboardEvent.preventDefault();
    keyboardEvent.stopPropagation();
    // Se o dialog de confirmacao estiver aberto, fecha apenas ele
    if (this.confirmationDialogRef) {
      this.confirmationDialogRef.close(false);
      this.confirmationDialogRef = null;
      return;
    }
    this.onClose();
  }

  ngOnInit() {
    this.capturedAccent = this.visualContext.accentForInputs(this.data?.accent,
      this.visualContext.accentForElement(this.element.nativeElement.parentElement));
    this.modalService.clearSubjects();
    this.modalService.getActionsSubject().subscribe(result => {
      if (result?.action === 'close') {
        this.modalRef.close(result.params['result']);
      }
    });
  }

  onCancel() {
    if (this.buttonCancelDisabled) return;
    if (this.beforeCancel && !this.beforeCancel()) {
      return;
    }
    if (this.closeConfirmation) {
      this.openCloseConfirmation();
    } else {
      this.modalRef.close(false);
    }
  }

  onClose() {
    if (this.buttonCloseDisabled) return;
    if (this.closeConfirmation) {
      this.openCloseConfirmation();
    } else {
      this.modalRef.close(false);
    }
  }

  openCloseConfirmation() {
    const mobile = window.innerWidth <= 640;
    const tablet = window.innerWidth > 640 && window.innerWidth <= 1024;
    const dialogConfig = {
      minWidth: mobile ? '80dvw' : tablet ? '50dvw' : '30dvw',
      maxWidth: mobile ? '95dvw' : tablet ? '65dvw' : '40dvw',
      minHeight: '20dvh',
      maxHeight: '80dvh',
      autoFocus: false,
      restoreFocus: false,
      panelClass: this.visualContext.classForInputs(this.accent, this.capturedAccent),
    };
    this.confirmationDialogRef = this.dialogService.open(this.closeConfirmationDialog, dialogConfig);

    this.confirmationDialogRef.afterClosed().subscribe(result => {
      this.confirmationDialogRef = null;
      if (result) {
        this.modalRef.close(false);
      }
    });
  }

  // Valida os formularios vinculados antes de confirmar
  confirmAndClose() {
    if (this.validateForms) {
      for (let form of this.validateForms) {
        if (!form.valid) {
          this.popupService.showAlert(this.msgFormInvalid);
          return;
        }
      }
    }
    if (this.beforeConfirm && !this.beforeConfirm()) {
      return;
    }
    this.modalRef.close(true);
  }

  ngOnDestroy() {
    this.modalService.clearSubjects();
    this.confirmationDialogRef = null;
  }
}
